import { useCallback, useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { Toaster, toast } from 'sonner'
import { CloudOff } from 'lucide-react'
import { missingRequirements } from '@/app/lib/capabilities'
import { subscribeFfmpeg } from '@/app/lib/ffmpeg'
import { onQueueEvent, startQueue } from '@/app/lib/local/queue'
import { onWatchEvent } from '@/app/lib/local/watcher'
import { useAppStore } from '@/app/store/useAppStore'

/**
 * Wraps the operator screens: checks what this browser can actually do, starts
 * the upload queue, and makes an incomplete setup unmistakable before anyone
 * starts a session rather than at the moment they press print.
 *
 * It used to hold a gate. `SetupScreen` covered the console whenever the camera
 * folder was not granted, which is every launch — Chrome downgrades a stored
 * handle to "prompt" across a browser restart — so the operator met a checklist
 * before the console every evening. The requirements did not go away with it;
 * they moved to where they can be acted on. This banner names what is missing
 * and leads to the Settings card that fixes it, and step 1 refuses to start
 * watching until nothing is (see lib/capabilities.ts).
 */
export function AppShell() {
  const navigate = useNavigate()
  const location = useLocation()

  const load = useAppStore((s) => s.load)
  const checkCapabilities = useAppStore((s) => s.checkCapabilities)
  const capabilities = useAppStore((s) => s.capabilities)
  const settings = useAppStore((s) => s.settings)
  const applyWatchEvent = useAppStore((s) => s.applyWatchEvent)
  const applyQueueEvent = useAppStore((s) => s.applyQueueEvent)
  const uploads = useAppStore((s) => s.uploads)
  const watchError = useAppStore((s) => s.watchError)

  const check = useCallback(async () => {
    await load()
    await checkCapabilities()
  }, [load, checkCapabilities])

  useEffect(() => {
    void check()
  }, [check])

  /**
   * Re-probe on every navigation.
   *
   * This is the whole reason removing the gate does not strand anyone: the
   * operator fixes a folder, a layout or the Raia account in Settings and comes
   * back, and the banner and the disabled buttons have to agree with what they
   * just did. A probe is a handful of IndexedDB reads and two permission
   * queries, so paying it per navigation is cheaper than any way of being told.
   */
  useEffect(() => {
    // The mount probe above owns the first one, and it waits for `load()`.
    // Racing it here would probe with `settings` still null and flash "Akun
    // Raia belum diatur" at an operator whose account is perfectly fine.
    if (!useAppStore.getState().capabilities) return
    void checkCapabilities()
  }, [location.pathname, checkCapabilities])

  // Downloading the encoder has to clear its own banner without a navigation,
  // so the shell follows the loader directly.
  useEffect(
    () =>
      subscribeFfmpeg((state) => {
        if (state.status === 'ready') void checkCapabilities()
      }),
    [checkCapabilities],
  )

  useEffect(() => onWatchEvent(applyWatchEvent), [applyWatchEvent])
  useEffect(() => onQueueEvent(applyQueueEvent), [applyQueueEvent])

  /**
   * The queue reads settings through a getter rather than a captured value, so
   * an operator who fills in their Raia account mid-event does not have to
   * reload before the sessions already queued start going up.
   */
  useEffect(() => startQueue(() => useAppStore.getState().settings), [])

  useEffect(() => {
    if (watchError) toast.error(watchError)
  }, [watchError])

  if (!capabilities) {
    return (
      <div className="grid h-full place-items-center text-ink-muted">
        <span className="citaria-live">Memeriksa perangkat…</span>
      </div>
    )
  }

  const missing = missingRequirements(capabilities, settings?.videoEnabled ?? true)
  const stalled = uploads.filter((upload) => upload.error)

  return (
    <div className="flex h-full flex-col bg-base">
      {/*
        The queue used to be a background service that survived this tab being
        closed. It is this tab now, so a session that cannot go up has to be
        visible rather than quietly retrying — a guest whose link never resolves
        has no way to tell anyone at the venue.
      */}
      {stalled.length > 0 && (
        <div className="flex shrink-0 items-center justify-center gap-2 bg-danger px-5 py-2 text-[14px] font-semibold text-black">
          <CloudOff size={16} />
          {stalled.length} sesi belum terunggah — biarkan konsol terbuka sampai selesai.
        </div>
      )}

      {/* A notice that only states a problem makes the operator hunt for the
          fix; this one is the way to it. Not shown on Settings itself, where
          every one of these has its own card saying the same thing better. */}
      {missing.length > 0 && location.pathname !== '/settings' && (
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="shrink-0 bg-warn/12 px-5 py-1.5 text-center text-[13px] text-warn active:bg-warn/20"
        >
          {missing.map((requirement) => requirement.detail).join(' · ')}
          <span className="ml-2 underline underline-offset-2">Perbaiki</span>
        </button>
      )}

      <Outlet />

      <Toaster theme="dark" position="top-center" richColors expand={false} />
    </div>
  )
}
